<template>
    <div class="deal-share">
        <div class="backend-links">
            <a href="/dealshare">Deal Share</a>
            <a href="/deal-list-manager">Manage Blog/Gift Lists</a>
            <a href="/logout">LOG OUT</a>
        </div>
        <h1>Manage Blog/Gift Lists</h1>
        <div class="deal-share-header">
            <div><input v-model="dealNumber" placeholder="Deal #" class="share-deal-input" @blur="clear()" /></div>
            <div v-if="latestDeals" class="latest-deals">
                <a @click="clear(); dealNumber = deal.dealId; generate();" v-for="deal in latestDeals" :key="deal.dealId">{{deal.dealId}}</a>
                <a @click="getLatestDeals()">REFRESH</a>
            </div>
            <div><textarea v-model="dealText" placeholder="Great Deal!" class="share-deal-input"></textarea></div>
            <div><textarea v-model="dealTitle" placeholder="Deal Title (optional)" class="share-deal-input"></textarea></div>
            <div><button class="button" :disabled="!dealNumber" @click="generate()">Generate</button></div>
            <div><img v-if="dealDetails" :src="dealDetails.imgUrl" style="max-height: 200px;" /></div>
        </div>
        <div v-show="isBusy">
            <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span> LOADING
        </div>
        <br />
        <TabMenu :tabs="tabs" :initialTab="initialTab">
            <template #tab-head-facebook>
                Facebook
            </template>
            <template #tab-panel-facebook>
                <div>
                    <div v-if="facebookMessage" class="deal-share-header">
                        <div class="share-message-container">
                            <textarea v-model="facebookMessage" id="facebookMessage" placeholder="" class="share-deal-input share-deal-share-message"></textarea>
                        </div>
                        <a v-show="!copied2" class="promo-copy-code" @click="copyToClipboard('facebookMessage')">COPY</a>
                        <a v-show="copied2" class="promo-copy-code copied">COPIED</a>
                    </div>
                </div>
                <div>
                    <div v-if="facebookMessage2" class="deal-share-header">
                        <div class="share-message-container">
                            <textarea v-model="facebookMessage2" id="facebookMessage2" placeholder="" class="share-deal-input share-deal-share-message"></textarea>
                        </div>
                        <a v-show="!copied3" class="promo-copy-code" @click="copyToClipboard('facebookMessage2')">COPY</a>
                        <a v-show="copied3" class="promo-copy-code copied">COPIED</a>
                    </div>
                </div>
            </template>

            <template #tab-head-telegram>
                Telegram
            </template>
            <template #tab-panel-telegram>
                <div>
                    <div v-if="dealDetails">
                        <textarea v-model="telegram.message" id="telegramMessage" placeholder="telegram custom message" class="share-deal-input share-deal-share-message"></textarea>
                        <div v-if="!telegram.isPosted"><button class="button" :disabled="telegram.isBusy" @click="postToTelegram()">Post to Telegram</button></div>
                        <div v-if="telegram.isPosted"><button class="button button-success" :disabled="telegram.isPosted">POSTED</button></div>
                    </div>
                </div>
            </template>

            <template #tab-head-whatsapp>
                WhatsApp
            </template>
            <template #tab-panel-whatsapp>
                <div>
                    <div v-if="shareMessage" class="deal-share-header">
                        <div class="share-message-container">
                            <textarea v-model="shareMessage" id="shareMessage" placeholder="" class="share-deal-input share-deal-share-message"></textarea>
                        </div>
                        <a v-show="!copied" class="promo-copy-code" @click="copyToClipboard('shareMessage')">COPY</a>
                        <a v-show="copied" class="promo-copy-code copied">COPIED</a>
                    </div>
                </div>
            </template>

            <template #tab-head-images>
                Story Images
            </template>
            <template #tab-panel-images>
                <div>
                    <div v-if="storyMessage" class="deal-share-header">
                        <div class="share-message-container">
                            <textarea v-model="storyMessage" id="storyMessage" placeholder="" class="share-deal-input share-deal-share-message"></textarea>
                        </div>
                        <a v-show="!copied3" class="promo-copy-code" @click="copyToClipboard('storyMessage')">COPY</a>
                        <a v-show="copied3" class="promo-copy-code copied">COPIED</a>
                    </div>
                </div>
                <div v-if="images" class="deal-share-results">
                    <img :src="image" v-for="image in images" :key="image" />
                </div>
            </template>

            <template #tab-head-raw>
                RAW
            </template>
            <template #tab-panel-raw>
                <div v-if="dealDetails" class="deal-share-results">
                    <textarea v-model="dealDetailsJson" class="share-deal-input share-deal-share-message" style="text-align: left"></textarea>
                </div>
            </template>
        </TabMenu>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import settings from '@/settings'
import api from '@/api'
import TabMenu from '@/components/TabMenu.vue'
import { DealDetails } from '@/classes/DealDetails'

export default defineComponent({
    name: 'GiftListManager',
    components: {
        TabMenu
    },
    setup() {
        const route = useRoute()
        const initialTab = ref('images')
        const latestDeals = ref<DealDetails[] | null>(null)
        const tabs = ref(['facebook', 'telegram', 'whatsapp', 'images', 'raw'])
        const isBusy = ref(false)
        const dealNumber = ref<number | null>(null)
        const dealText = ref('Great Deal!')
        const dealTitle = ref<string | null>(null)
        const images = ref<string[] | null>(null)
        const results = ref(false)
        const dealDetails = ref<DealDetails | null>(null)
        const shareMessage = ref<string | null>(null)
        const facebookMessage = ref<string | null>(null)
        const facebookMessage2 = ref<string | null>(null)
        const storyMessage = ref<string | null>(null)
        const copied = ref(false)
        const copied2 = ref(false)
        const copied3 = ref(false)
        const copied4 = ref(false)
        const telegram = ref({
            message: null as string | null,
            isPosted: false,
            isBusy: false
        })

        const getLatestDeals = async () => {
            const response = await api.getLatestDeals(5, 1, null)
            latestDeals.value = response.data
        }

        const clear = () => {
            dealText.value = 'Great Deal!'
            dealTitle.value = null
        }

        const copyToClipboard = (textboxName: string) => {
            const copyText = document.getElementById(textboxName) as HTMLTextAreaElement
            copyText.select()
            document.execCommand("copy")

            switch (textboxName) {
                case 'shareMessage':
                    copied.value = true
                    setTimeout(() => {
                        copied.value = false
                    }, 2000)
                    break
                case 'facebookMessage':
                    copied2.value = true
                    setTimeout(() => {
                        copied2.value = false
                    }, 2000)
                    break
                case 'facebookMessage2':
                    copied3.value = true
                    setTimeout(() => {
                        copied3.value = false
                    }, 2000)
                    break
                case 'storyMessage':
                    copied4.value = true
                    setTimeout(() => {
                        copied4.value = false
                    }, 2000)
                    break
            }
        }

        const getPercentOff = (deal: DealDetails): number | null => {
            if (deal.retailPrice && deal.dealPrice && deal.retailPrice >= 0) {
                return Math.round((deal.retailPrice - deal.dealPrice) / deal.retailPrice * 100.00)
            }
            return null
        }

        const getImages = (): string[] => {
            return [
                `/storyimage/${dealNumber.value}.jpg?color=pink&title=${encodeURIComponent(dealTitle.value || '')}&message=${encodeURIComponent(dealText.value)}`,
                `/storyimage/${dealNumber.value}.jpg?color=fushia&title=${encodeURIComponent(dealTitle.value || '')}&message=${encodeURIComponent(dealText.value)}`,
                `/storyimage/${dealNumber.value}.jpg?color=orange&title=${encodeURIComponent(dealTitle.value || '')}&message=${encodeURIComponent(dealText.value)}`,
                `/storyimage/${dealNumber.value}.jpg?color=purple&title=${encodeURIComponent(dealTitle.value || '')}&message=${encodeURIComponent(dealText.value)}`,
                `/storyimage/${dealNumber.value}.jpg?color=blue&title=${encodeURIComponent(dealTitle.value || '')}&message=${encodeURIComponent(dealText.value)}`,
                `/storyimage/${dealNumber.value}.jpg?color=green&title=${encodeURIComponent(dealTitle.value || '')}&message=${encodeURIComponent(dealText.value)}`
            ]
        }

        const postToTelegram = async () => {
            telegram.value.isBusy = true
            try {
                await api.postToTelegram({ dealId: dealNumber.value, message: telegram.value.message })
                telegram.value.isPosted = true
            } finally {
                telegram.value.isBusy = false
            }
        }

        const generate = async () => {
            if (!dealNumber.value) return

            isBusy.value = true
            results.value = false
            shareMessage.value = null

            try {
                const response = await api.getDeal(dealNumber.value)
                if (response && response.data) {
                    dealDetails.value = response.data
                    
                    if (!dealTitle.value && dealDetails.value.title) {
                        dealTitle.value = dealDetails.value.title
                    }

                    const dealLink = `https://${settings.siteUrl}/Deal/${dealNumber.value}`
                    const retailPrice = dealDetails.value.retailPrice?.toFixed(2)
                    const salePrice = dealDetails.value.dealPrice?.toFixed(2)
                    const percentOff = getPercentOff(dealDetails.value)

                    const facebook_post = `${dealText.value} Only $${salePrice}!
👉 ${dealLink}`

                    let whatsapp_post = `*${dealText.value}*

~Was $${retailPrice}~ *NOW $${salePrice}*

${dealTitle.value}
👉 ${dealLink}

*_Share this deal with friends!_*
🤱 Link to WhatsApp group: *${settings.socialLinks.whatsapp}*`

                    if (!retailPrice) {
                        whatsapp_post = `${dealText.value} *ONLY $${salePrice}*

${dealTitle.value}
👉 ${dealLink}

*_Share this deal with friends!_*
🤱 Link to WhatsApp group: *${settings.socialLinks.whatsapp}*`
                    }

                    let story_post = `*${dealText.value}*

~Was $${retailPrice}~ *NOW $${salePrice}*

${dealTitle.value}
👉 ${dealLink}`

                    if (!retailPrice) {
                        story_post = `${dealText.value} 

*ONLY $${salePrice}*

${dealTitle.value}
👉 ${dealLink}`
                    }

                    shareMessage.value = whatsapp_post
                    facebookMessage.value = facebook_post
                    storyMessage.value = story_post

                    if (percentOff) {
                        const facebook_post2 = `${dealText.value} ${percentOff}% OFF!
👉 ${dealLink}`
                        facebookMessage2.value = facebook_post2
                    } else {
                        facebookMessage2.value = null
                    }

                    telegram.value.message = dealText.value
                    telegram.value.isPosted = false

                    images.value = getImages()
                }
            } finally {
                isBusy.value = false
            }
        }

        const dealDetailsJson = computed(() => {
            return JSON.stringify(dealDetails.value)
        })

        onMounted(() => {
            if (route.query.dealNumber) {
                dealNumber.value = Number(route.query.dealNumber)
                generate()
            }
            getLatestDeals()
        })

        return {
            initialTab,
            latestDeals,
            tabs,
            isBusy,
            dealNumber,
            dealText,
            dealTitle,
            images,
            results,
            dealDetails,
            shareMessage,
            facebookMessage,
            facebookMessage2,
            storyMessage,
            copied,
            copied2,
            copied3,
            copied4,
            telegram,
            dealDetailsJson,
            getLatestDeals,
            clear,
            copyToClipboard,
            generate,
            postToTelegram
        }
    }
})
</script>

<style>
</style>